import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';

const About = () => {
  return (
    <div className="min-h-screen pt-20 bg-black" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                <img
                  src="https://static.wixstatic.com/media/11ba4a_af39caa302e948fdb3d4494c540bc884~mv2.jpg/v1/fill/w_600,h_400,al_c,q_80,enc_avif,quality_auto/11ba4a_af39caa302e948fdb3d4494c540bc884~mv2.jpg"
                  alt="Jim Richardson at work"
                  className="w-full h-auto shadow-soft border border-white/10"
                  data-testid="about-image"
                />
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-teal-500/10 -z-10" />
              </div>
            </div>

            {/* Text */}
            <div className="order-1 lg:order-2">
              <p className="text-teal-400 uppercase tracking-[0.3em] text-xs font-medium mb-4">
                About the Artist
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-white mb-6">
                Jim Richardson
              </h1>
              <div className="flex items-center text-white/50 text-sm mb-8">
                <MapPin className="h-4 w-4 mr-2" />
                <span>Victoria, BC, Canada</span>
              </div>
              <p className="text-white/70 text-lg leading-relaxed">
                Creating stone on glass art designed for public spaces. Modern, 
                simple, clean, yet warm because of the natural stone. Each piece 
                is a meditation on balance, nature, and the interplay between 
                solid and transparent.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Medium Section */}
      <section className="py-16 lg:py-24 bg-black" data-testid="medium-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-teal-400 uppercase tracking-[0.3em] text-xs font-medium mb-4">
              The Medium
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white">
              Stone on Glass
            </h2>
          </div>

          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              Just stones on glass, suspended over a board. The stones are mostly 
              from Dallas Road beach near Cook Street in Victoria BC, Canada. Some 
              of the glass pieces were found objects.
            </p>

            <p className="text-white/70 text-lg leading-relaxed">
              The contrast between the ancient, weathered stones and the modern 
              transparency of glass creates a unique visual language. Each piece 
              captures a moment of balance—the weight of earth held in suspension, 
              light passing through and around solid forms.
            </p>
          </div>
        </div>
      </section>

      {/* Origin Story Section */}
      <section className="py-16 lg:py-24 bg-neutral-950" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text */}
            <div>
              <p className="text-teal-400 uppercase tracking-[0.3em] text-xs font-medium mb-4">
                What Started It All
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-8">
                The Inspiration
              </h2>
              
              <div className="space-y-6 text-white/70 leading-relaxed">
                <p>
                  The <a href="https://floathousevictoria.com/" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:underline">Float House Victoria</a> offered 
                  two free floats in their Sensory Deprivation Floatation Tanks to 
                  artists who would make a piece for the art book they were publishing.
                </p>
                
                <p>
                  After my floats, I noticed the beauty in the water drops running 
                  down the glass shower. Trying to find a medium I could work with 
                  led me to the pebbles on the beach and the glass that I put them on.
                </p>
                
                <p>
                  The dripping stones you see in some of the pieces come from that 
                  formative experience. I just couldn't stop after one piece!
                </p>
              </div>
            </div>

            {/* Image */}
            <div>
              <img
                src="https://static.wixstatic.com/media/11ba4a_f6cccf1b9b3d49af8d8ed93bf4e76d60.jpg/v1/fill/w_800,h_300,al_c,q_80,enc_avif,quality_auto/11ba4a_f6cccf1b9b3d49af8d8ed93bf4e76d60.jpg"
                alt="Dallas Road Beach Victoria"
                className="w-full h-auto shadow-soft border border-white/10"
                data-testid="beach-image"
              />
              <p className="text-white/40 text-sm mt-4 text-center">
                Dallas Road Beach, Victoria BC
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Goal Section */}
      <section className="py-16 lg:py-24 bg-teal-600" data-testid="goal-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-black/60 uppercase tracking-[0.3em] text-xs font-medium mb-4">
            My Goal
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-black mb-8">
            Art for Public Spaces
          </h2>
          <p className="text-black/70 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            I love public art. I want my art to hang in public places. If you know 
            of a public space that would be improved by some art... let me know.
          </p>
          <Link to="/contact" data-testid="contact-cta-btn">
            <Button className="bg-black text-white hover:bg-neutral-900 px-8 py-3 rounded-sm uppercase tracking-widest text-xs font-semibold transition-all">
              Get in Touch
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* View Portfolio CTA */}
      <section className="py-16 lg:py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-white mb-6">
            See the Collection
          </h2>
          <p className="text-white/60 mb-8">
            Explore the full portfolio of stone on glass artworks.
          </p>
          <Link to="/portfolio" data-testid="portfolio-cta-btn">
            <Button variant="outline" className="btn-secondary">
              View Portfolio
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
